import React, { forwardRef } from "react";

import { Form } from "@weezevent/nacre";
import { useController, useFormContext } from "react-hook-form";
import PropTypes from "prop-types";

import { getFormErrorMessages } from "helpers/form";

/**
 * This component is an input to manage a select with multiple options.
 * It's a controlled field that deals with react-hook-form. Must be wrapped by a FormContext.
 */
const FormFieldInput = forwardRef(
	(
		{
			name,
			rules,
			defaultValue,
			label,
			control: controlProp,
			onChange: onExternalChange,
			...props
		},
		ref
	) => {
		// Get control from context if not provided as prop
		const formContext = useFormContext();
		const control = controlProp || (formContext && formContext.control);

		const {
			field: { onChange, value },
			fieldState: { error }
		} = useController({
			name,
			rules,
			defaultValue,
			control
		});

		const onChangeValue = React.useCallback(
			e => {
				const newValue = e.target.value;
				onChange(newValue);
				if (onExternalChange) {
					onExternalChange(newValue);
				}
			},
			[onChange, onExternalChange]
		);

		const errorMessages = getFormErrorMessages(error);

		return (
			<Form.Field>
				{!!label && <Form.Label>{label}</Form.Label>}
				<Form.Input
					ref={ref}
					name={name}
					onChange={onChangeValue}
					value={value}
					required={!!rules.required}
					errors={!!error}
					/* eslint-disable-next-line react/jsx-props-no-spreading */
					{...props}
				/>
				{errorMessages && <Form.FieldErrors errors={errorMessages} />}
			</Form.Field>
		);
	}
);

FormFieldInput.propTypes = {
	/**
	 * The name of the field. This name must be unique in the form.
	 * Used by react-hook-form.
	 */
	name: PropTypes.string.isRequired,
	/**
	 * The default value of the field.
	 * Used by react-hook-form.
	 */
	defaultValue: PropTypes.string,
	/**
	 * The object of rules to apply to validate the field.
	 * Used by react-hook-form.
	 */
	rules: PropTypes.object,
	/**
	 * The label of the field to display.
	 */
	label: PropTypes.node,
	control: PropTypes.object,
	onChange: PropTypes.func
};

FormFieldInput.defaultProps = {
	defaultValue: "",
	rules: {},
	label: undefined,
	control: undefined,
	onChange: () => {}
};

export default FormFieldInput;
